import { useCallback, useRef } from 'react';

/**
 * Tracks which form fields have been manually edited by the user.
 * This is useful for auto-fill features where we only want to populate
 * fields that haven't been touched by the user.
 */
export function useFieldDirtyState<TFieldNames extends string>(initialDirtyFields: Readonly<TFieldNames[]> = []) {
  // Using a ref to avoid re-renders when dirty state changes
  const dirtyFieldsRef = useRef<Set<TFieldNames>>(new Set(initialDirtyFields));

  /**
   * Mark a field as dirty (user has manually edited it)
   */
  const markFieldDirty = useCallback((fieldName: TFieldNames) => {
    dirtyFieldsRef.current.add(fieldName);
  }, []);

  /**
   * Mark a field as clean (allow auto-fill to populate it)
   */
  const markFieldClean = useCallback((fieldName: TFieldNames) => {
    dirtyFieldsRef.current.delete(fieldName);
  }, []);

  /**
   * Check if a field is dirty (has been manually edited)
   */
  const isFieldDirty = useCallback((fieldName: TFieldNames): boolean => {
    return dirtyFieldsRef.current.has(fieldName);
  }, []);

  /**
   * Reset all dirty states
   */
  const resetDirtyState = useCallback(() => {
    dirtyFieldsRef.current.clear();
  }, []);

  /**
   * Get all dirty field names
   */
  const getDirtyFields = useCallback((): TFieldNames[] => {
    return Array.from(dirtyFieldsRef.current);
  }, []);

  return {
    markFieldDirty,
    markFieldClean,
    isFieldDirty,
    resetDirtyState,
    getDirtyFields,
  } as const;
}

export type UseFieldDirtyStateResult<TFieldNames extends string> = Readonly<{
  markFieldDirty: (fieldName: TFieldNames) => void;
  markFieldClean: (fieldName: TFieldNames) => void;
  isFieldDirty: (fieldName: TFieldNames) => boolean;
  resetDirtyState: () => void;
  getDirtyFields: () => TFieldNames[];
}>;
